import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
  CircularProgress,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Image as ImageIcon,
  AudioFile as AudioFileIcon,
  VideoFile as VideoFileIcon,
  InsertDriveFile as FileIcon,
  ContentCopy as ContentCopyIcon,
  CloudUpload as CloudUploadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store';
import { MediaAsset } from '../../../types/media.types';
import {
  deleteMediaAsset,
  setPage,
  setRowsPerPage,
  fetchMediaAssets,
  fetchMediaAssetsByPublisher,
  createMediaAsset,
} from '../slices/mediaSlice';
import ConfirmDialog from './common/ConfirmDialog';
import MediaForm from './MediaForm';

interface MediaTableProps {
  publisherId?: string;
}

const MediaTable: React.FC<MediaTableProps> = ({ publisherId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { filteredItems, isLoading, pagination } = useSelector((state: RootState) => state.media);

  const [openForm, setOpenForm] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState<MediaAsset | undefined>(undefined);
  const [isEdit, setIsEdit] = useState(false);
  const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
  const [mediaToDelete, setMediaToDelete] = useState<MediaAsset | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  
  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [fileCount, setFileCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState<{
    successful: string[];
    failed: { name: string; error: string }[];
  } | null>(null);
  const [showUploadSummary, setShowUploadSummary] = useState(false);
  const [showFaviconHelp, setShowFaviconHelp] = useState(false);

  useEffect(() => {
    if (publisherId) {
      dispatch(fetchMediaAssetsByPublisher(publisherId));
    } else {
      dispatch(fetchMediaAssets());
    }
  }, [dispatch, publisherId]);

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setRowsPerPage(parseInt(event.target.value, 10)));
  };

  const handleAddClick = () => {
    setIsEdit(false);
    setSelectedMedia(undefined);
    setOpenForm(true);
  };

  const handleOpenEdit = (media: MediaAsset) => {
    setIsEdit(true);
    setSelectedMedia(media);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
  };

  const handleDeleteClick = (media: MediaAsset, event: React.MouseEvent) => {
    event.stopPropagation();
    setMediaToDelete(media);
    setOpenConfirmDelete(true);
  };

  const handleConfirmDelete = async () => {
    if (mediaToDelete) {
      try {
        await dispatch(deleteMediaAsset(mediaToDelete.id));
        setOpenConfirmDelete(false);
        setMediaToDelete(null);
      } catch (error) {
        console.error('Error deleting media asset:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setOpenConfirmDelete(false);
    setMediaToDelete(null);
  };

  const handleRowClick = (media: MediaAsset) => {
    handleOpenEdit(media);
  };

  const handleDownload = (media: MediaAsset, event: React.MouseEvent) => {
    event.stopPropagation();
    const downloadUrl = media.publicUrl || media.s3Url || media.fileUrl;
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const handleCopyUrl = async (media: MediaAsset, event: React.MouseEvent) => {
    event.stopPropagation();
    const copyUrl = media.publicUrl || media.s3Url || media.fileUrl;
    if (copyUrl) {
      try {
        await navigator.clipboard.writeText(copyUrl);
        setCopySuccess(true);
      } catch (error) {
        console.error('Failed to copy URL:', error);
      }
    }
  };

  const handleCloseSnackbar = () => {
    setCopySuccess(false);
  };

  // Generate a friendly name from filename
  const generateNameFromFile = (filename: string): string => {
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    return nameWithoutExt
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Generate description based on file type
  const generateDescription = (file: File): string => {
    const type = file.type.split('/')[0];
    const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
    
    switch (type) {
      case 'image':
        return `Image file (${sizeInMB} MB)`;
      case 'audio':
        return `Audio file (${sizeInMB} MB)`;
      case 'video':
        return `Video file (${sizeInMB} MB)`;
      default:
        return `Media file (${sizeInMB} MB)`;
    }
  };

  // Handle drag events
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!publisherId || isUploading) return;
    
    const newCounter = dragCounter + 1;
    setDragCounter(newCounter);
    
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      const files = Array.from(e.dataTransfer.items).filter(
        (item) => item.kind === 'file'
      );
      setFileCount(files.length);
      setIsDragging(true);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDragCounter((prev) => {
      const newCounter = prev - 1;
      if (newCounter === 0) {
        setIsDragging(false);
        setFileCount(0);
      }
      return newCounter;
    });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(false);
    setDragCounter(0);
    setFileCount(0);
    
    if (!publisherId || isUploading) return;
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    
    await uploadFiles(files);
  };

  const uploadFiles = async (files: File[]) => {
    setIsUploading(true);
    setUploadProgress(0);
    
    const successful: string[] = [];
    const failed: { name: string; error: string }[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      try {
        const mediaData = {
          name: generateNameFromFile(file.name),
          description: generateDescription(file),
          publisherPK: publisherId!,
          mimeType: file.type || 'application/octet-stream',
          category: file.type.split('/')[0] || 'other',
          keywords: [],
          file: file,
        };
        
        await dispatch(createMediaAsset(mediaData)).unwrap();
        successful.push(file.name);
      } catch (error: any) {
        failed.push({
          name: file.name,
          error: error.message || 'Upload failed',
        });
      }
      
      setUploadProgress(((i + 1) / files.length) * 100);
    }
    
    setIsUploading(false);
    setUploadResults({ successful, failed });
    setShowUploadSummary(true);
    
    // Refresh the media list
    if (publisherId) {
      dispatch(fetchMediaAssetsByPublisher(publisherId));
    }
  };

  const handleCloseSummary = () => {
    setShowUploadSummary(false);
    setUploadResults(null);
    setUploadProgress(0);
  };

  const renderPreview = (media: MediaAsset) => {
    const mimeType = media.mimeType?.toLowerCase() || '';

    if (mimeType.startsWith('image/')) {
      const imageUrl = media.publicUrl || media.s3Url || media.thumbnailUrl || media.fileUrl;
      if (!imageUrl) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ImageIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              No preview
            </Typography>
          </Box>
        );
      }
      return (
        <Box
          component="img"
          src={imageUrl}
          alt={media.name}
          sx={{
            maxWidth: 80,
            maxHeight: 60,
            objectFit: 'contain',
            borderRadius: 1,
          }}
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              parent.innerHTML = '<div style="display: flex; align-items: center; gap: 8px;"><svg style="width: 24px; height: 24px; color: rgba(0,0,0,0.54);" viewBox="0 0 24 24"><path fill="currentColor" d="M21,19V5c0,-1.1 -0.9,-2 -2,-2H5c-1.1,0 -2,0.9 -2,2v14c0,1.1 0.9,2 2,2h14c1.1,0 2,-0.9 2,-2zM8.5,13.5l2.5,3.01L14.5,12l4.5,6H5l3.5,-4.5z"/></svg><span style="font-size: 0.875rem; color: rgba(0,0,0,0.6);">Preview failed</span></div>';
            }
          }}
        />
      );
    }

    if (mimeType.startsWith('audio/')) {
      const audioUrl = media.publicUrl || media.s3Url || media.fileUrl;
      if (!audioUrl) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AudioFileIcon color="action" />
            <Typography variant="body2" color="text.secondary">
              No preview
            </Typography>
          </Box>
        );
      }
      return (
        <Box sx={{ width: 200 }}>
          <audio
            controls
            style={{ width: '100%', height: 32 }}
            onClick={(e) => e.stopPropagation()}
          >
            <source src={audioUrl} type={media.mimeType} />
            Your browser does not support the audio element.
          </audio>
        </Box>
      );
    }

    if (mimeType.startsWith('video/')) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VideoFileIcon color="action" />
          <Typography variant="body2" color="text.secondary">
            Video preview not available
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FileIcon color="action" />
        <Typography variant="body2" color="text.secondary">
          No preview available
        </Typography>
      </Box>
    );
  };

  const getFileIcon = (mimeType: string) => {
    const type = mimeType?.toLowerCase() || '';
    if (type.startsWith('image/')) return <ImageIcon />;
    if (type.startsWith('audio/')) return <AudioFileIcon />;
    if (type.startsWith('video/')) return <VideoFileIcon />;
    return <FileIcon />;
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const items = filteredItems;
  const displayedItems = items.slice(
    pagination.page * pagination.rowsPerPage,
    pagination.page * pagination.rowsPerPage + pagination.rowsPerPage
  );

  return (
    <Box
      sx={{ width: '100%', position: 'relative' }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag and Drop Overlay */}
      {isDragging && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            border: '3px dashed #1976d2',
            borderRadius: 2,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
            Drop {fileCount} file{fileCount !== 1 ? 's' : ''} to upload
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Media assets will be created automatically
          </Typography>
        </Box>
      )}

      {/* Upload Progress Overlay */}
      {isUploading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={60} sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 2 }}>
            Uploading Media Assets...
          </Typography>
          <Box sx={{ width: '50%', mb: 1 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {Math.round(uploadProgress)}% complete
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" component="h2">
            Media Assets {publisherId ? 'for Selected Publisher' : ''}
          </Typography>
          {publisherId && !isUploading && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Drag and drop files here to upload multiple media assets at once
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setShowFaviconHelp(true)}
            disabled={isLoading || !publisherId || isUploading}
          >
            Favicon Help
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={isLoading || !publisherId || isUploading}
          >
            Upload Media
          </Button>
        </Box>
      </Box>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Preview</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Size</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : displayedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body1" color="text.secondary">
                      No media assets found.
                    </Typography>
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      onClick={handleAddClick}
                      sx={{ mt: 1 }}
                      disabled={!publisherId}
                    >
                      Upload your first media asset
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                displayedItems.map((media) => (
                  <TableRow
                    key={media.id}
                    hover
                    onClick={() => handleRowClick(media)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{renderPreview(media)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getFileIcon(media.mimeType)}
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                            {media.name}
                          </Typography>
                          {media.description && (
                            <Typography variant="body2" color="text.secondary">
                              {media.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={media.mimeType} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      {media.category ? (
                        <Chip label={media.category} size="small" color="primary" variant="outlined" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>{formatFileSize(media.fileSizeBytes)}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Copy URL">
                          <IconButton
                            color="primary"
                            onClick={(event) => handleCopyUrl(media, event)}
                            disabled={!media.publicUrl && !media.s3Url && !media.fileUrl}
                          >
                            <ContentCopyIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton
                            color="primary"
                            onClick={(event) => handleDownload(media, event)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            onClick={(event) => handleDeleteClick(media, event)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={items.length}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      <MediaForm
        open={openForm}
        onClose={handleCloseForm}
        media={selectedMedia}
        isEdit={isEdit}
        publisherId={publisherId}
      />

      <ConfirmDialog
        open={openConfirmDelete}
        title="Confirm Delete"
        message={`Are you sure you want to delete ${mediaToDelete?.name}?`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Upload Summary Dialog */}
      <Dialog
        open={showUploadSummary}
        onClose={handleCloseSummary}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Summary</DialogTitle>
        <DialogContent>
          {uploadResults && (
            <>
              {uploadResults.successful.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" color="success.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                    ✓ Successfully Uploaded ({uploadResults.successful.length})
                  </Typography>
                  <List dense>
                    {uploadResults.successful.map((filename, index) => (
                      <ListItem key={index}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon color="success" />
                        </ListItemIcon>
                        <ListItemText primary={filename} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              
              {uploadResults.failed.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" color="error.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                    ✗ Failed to Upload ({uploadResults.failed.length})
                  </Typography>
                  <List dense>
                    {uploadResults.failed.map((item, index) => (
                      <ListItem key={index}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <ErrorIcon color="error" />
                        </ListItemIcon>
                        <ListItemText
                          primary={item.name}
                          secondary={item.error}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSummary} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          URL copied to clipboard!
        </Alert>
      </Snackbar>

      {/* Favicon Help Dialog */}
      <Dialog
        open={showFaviconHelp}
        onClose={() => setShowFaviconHelp(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpOutlineIcon color="primary" />
            <Typography variant="h6" component="span">
              Favicon Help Guide
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              What is a Favicon?
            </Typography>
            <Typography variant="body1" paragraph>
              A favicon (short for "favorite icon") is a small icon that represents your IssueCast page. 
              It appears in multiple places across different devices and browsers to help users quickly 
              identify and navigate to your content.
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Where Favicons Appear
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="• Browser tabs - next to your page title"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• Bookmarks and favorites lists"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• Browser history"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• Address bar (some browsers)"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• Mobile device home screens (when saved as shortcut)"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• Search engine results (in some cases)"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• Task switchers on mobile devices"
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            </List>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              How It Works
            </Typography>
            <Typography variant="body1" paragraph>
              Upload media assets with specific filenames (listed below) and they will automatically be 
              used as favicon images for your IssueCast page. You can upload all of them for maximum 
              compatibility, or just the most important ones. If no favicon files are provided, your 
              IssueCast page will use the default Location Station favicon.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              Favicon Filenames & Usage
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph sx={{ fontStyle: 'italic' }}>
              Files marked with ⭐ are the most important due to their widespread use across browsers and devices.
            </Typography>
          </Box>

          <Box sx={{ mb: 3, pl: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                ⭐ favicon.ico
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The classic favicon format. Used by all browsers, especially older versions. Should contain 
                16x16 and 32x32 pixel versions. This is the most universally compatible format.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                favicon.svg
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Modern vector format that scales perfectly at any size. Supported by modern browsers 
                (Chrome, Firefox, Safari). Provides the best quality and smallest file size.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                favicon-16x16.png
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Small PNG version for browser tabs and bookmarks. Used when .ico is not available.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                favicon-32x32.png
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Medium PNG version for higher resolution displays and larger browser UI elements.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                ⭐ apple-touch-icon.png
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Used when iOS users save your page to their home screen (180x180px recommended). This is 
                essential for Apple devices including iPhone, iPad, and iPod Touch. High market share makes 
                this very important.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                ⭐ android-chrome-192x192.png
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Standard icon for Android Chrome home screen shortcuts and Progressive Web Apps. Critical 
                for Android users (192x192px).
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                ⭐ android-chrome-512x512.png
              </Typography>
              <Typography variant="body2" color="text.secondary">
                High-resolution icon for Android Chrome splash screens and app install prompts (512x512px). 
                Required for Progressive Web Apps.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                maskable-icon-192x192.png
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Adaptive icon for Android that works with various shaped icon masks (circle, squircle, rounded 
                square). Ensures your icon looks good on all Android launchers.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                maskable-icon-512x512.png
              </Typography>
              <Typography variant="body2" color="text.secondary">
                High-resolution adaptive icon for Android. Should have important content in the center "safe zone" 
                to avoid cropping.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                safari-pinned-tab.svg
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monochrome SVG icon for Safari's pinned tabs on macOS. Should be a single color silhouette 
                with transparent background.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                site.webmanifest
              </Typography>
              <Typography variant="body2" color="text.secondary">
                JSON manifest file that describes your web app to browsers. References the Android Chrome icons 
                and defines app name, colors, and display preferences for Progressive Web Apps.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                mstile-150x150.png
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tile icon for Windows Start Menu when users pin your site (150x150px). Less critical now 
                but still used by some Windows users.
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                browserconfig.xml
              </Typography>
              <Typography variant="body2" color="text.secondary">
                XML configuration file for Windows 8/10 tile icons. References the mstile image and defines 
                tile colors.
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2, p: 2, bgcolor: 'info.lighter', borderRadius: 1, border: '1px solid', borderColor: 'info.light' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              💡 Recommendation
            </Typography>
            <Typography variant="body2">
              For best results, upload all favicon files. However, if you need to prioritize, focus on these 
              high-impact files based on market share:
            </Typography>
            <Box sx={{ mt: 1, pl: 2 }}>
              <Typography variant="body2">
                1. <strong>favicon.ico</strong> - Universal browser support
              </Typography>
              <Typography variant="body2">
                2. <strong>apple-touch-icon.png</strong> - iOS devices (high market share)
              </Typography>
              <Typography variant="body2">
                3. <strong>android-chrome-192x192.png</strong> - Android devices
              </Typography>
              <Typography variant="body2">
                4. <strong>android-chrome-512x512.png</strong> - Android PWA support
              </Typography>
              <Typography variant="body2">
                5. <strong>favicon.svg</strong> - Modern browsers, best quality
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFaviconHelp(false)} variant="contained">
            Got It
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaTable;
